/* eslint-disable sort-keys-fix/sort-keys-fix */
import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import CheckboxRoundedIcon from '@mui/icons-material/CheckBoxRounded';
import ErrorIcon from '@mui/icons-material/Error';
import { MIconMenu } from 'library';
import { arrayOf, func, shape } from 'prop-types';
import * as R from 'ramda';
import { useUserRolePermissions } from 'hooks';

const CustomerAuthCard = ({
  customerAuthorization,
  fetchCustomerAuthorization,
  selectedAuthorizations,
  setActivationDialogOpen,
  setConfirmDialogOpen,
  setConfirmDialogType,
  setCustomerAuthorizationId,
  setCustomerAuthorizationToActivate,
  setCustomerAuthorizationToDeactivate,
  setIsViewDrawerOpen,
  setSelectedAuthorizations,
}) => {
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });
  const {
    authorizationStatus,
    customerClassName,
    controls,
    controlType,
    customerId,
    customerIdName,
    terminals,
    customerName,
    products,
  } = customerAuthorization;
  const { isProductLoadingManage, isSupplier } = useUserRolePermissions();

  const inactiveLocations = terminals.filter(
    terminal => terminal.terminalLoadingStatus === 'Inactive'
  );

  const isSelected =
    selectedAuthorizations.filter(auth => auth.customerId === customerId).length > 0;

  const handleChange = () =>
    isSelected
      ? setSelectedAuthorizations(
          R.filter(auth => auth.customerId !== customerId, selectedAuthorizations)
        )
      : setSelectedAuthorizations([...selectedAuthorizations, customerAuthorization]);

  const menuItems = [
    {
      label: 'View',
      onClick: () => {
        setIsViewDrawerOpen(true);
        fetchCustomerAuthorization({ customerAuthorizationId: customerId });
      },
      value: 'view',
    },
  ];

  const authedMenuItems = [
    {
      label: 'Edit',
      onClick: () => setCustomerAuthorizationId(customerId),
      value: 'edit',
    },
    {
      label: authorizationStatus === 'Active' ? 'Deactivate' : 'Activate',
      onClick: () => {
        if (authorizationStatus === 'Active') {
          setCustomerAuthorizationToDeactivate(customerAuthorization);
          setConfirmDialogOpen(true);
          setConfirmDialogType('deactivate');
        } else {
          setCustomerAuthorizationToActivate(customerAuthorization);
          setActivationDialogOpen(true);
        }
      },
      value: authorizationStatus === 'Active' ? 'deactivate' : 'activate',
    },
  ];

  const getMenuItems = () =>
    isProductLoadingManage && isSupplier ? R.concat(menuItems, authedMenuItems) : menuItems;

  return (
    <Card variant="table">
      <CardContent
        sx={{
          ':last-child': { paddingBottom: 1 },
          alignItems: lgUp ? 'center' : 'start',
          display: 'flex',
          padding: 1,
        }}
      >
        {isSupplier && isProductLoadingManage && (
          <Checkbox
            checked={isSelected}
            checkedIcon={<CheckboxRoundedIcon sx={{ color: 'secondary.main' }} />}
            onChange={handleChange}
          />
        )}

        <Box
          direction="row"
          flex={1}
          justifyContent="space-between"
          p={1}
          sx={{
            display: { lg: 'flex' },
          }}
        >
          <Stack
            sx={{
              borderRight: { lg: '1px solid' },
              borderRightColor: { lg: 'graphic.light' },
              flex: { lg: 3 },
              justifyContent: { lg: 'center' },
              marginRight: { lg: 3 },
            }}
          >
            <Typography
              sx={{
                color: 'text.secondary',
                mb: { xs: 0, lg: 0 },
              }}
              variant="caption"
            >
              Cust ID #{customerIdName}
            </Typography>
            <Tooltip enterTouchDelay={10} title={customerName}>
              <Typography component="p" variant="body1bold">
                {customerName}
              </Typography>
            </Tooltip>
          </Stack>
          <Stack
            sx={{
              alignItems: { lg: 'start' },
              display: { xs: 'block', lg: 'flex' },
              flex: { lg: 1 },
              justifyContent: { lg: 'start' },
              marginRight: { lg: 1 },
            }}
          >
            {lgUp && (
              <Typography mb={0.5} variant="caption">
                Status
              </Typography>
            )}
            <Chip
              label={authorizationStatus}
              sx={theme => ({
                ...theme.typography.caption,
                '.MuiChip-label': {
                  paddingX: 1,
                },
                backgroundColor:
                  authorizationStatus === 'Active' ? 'success.light' : 'graphic.light',
                height: 20,
                lineHeight: '16px',
                margin: '2px 0',
              })}
            />
          </Stack>
          <Stack
            direction="row"
            sx={{
              flex: 3,
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              justifyContent: { xs: 'space-between', lg: 'space-evenly' },
              mb: 0.5,
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              mt: { xs: 1, lg: 0 },
            }}
          >
            <Stack
              direction={lgUp ? 'column' : 'row'}
              flex={1}
              sx={{
                alignItems: { xs: 'center', lg: 'start' },
                marginRight: { lg: 1 },
              }}
            >
              <Typography sx={{ mb: { xs: 0, lg: 0.5 }, mr: 0.5 }} variant="caption">
                {!lgUp ? 'Prod.' : 'Products'}
              </Typography>

              <Typography sx={{ padding: { lg: '3px 0' } }} variant="body2">
                {products.length}
              </Typography>
            </Stack>
            <Stack
              direction={lgUp ? 'column' : 'row'}
              flex={1}
              sx={{
                alignItems: { xs: 'center', lg: 'start' },
                marginRight: { lg: 1 },
              }}
            >
              <Typography sx={{ mb: { xs: 0, lg: 0.5 }, mr: 0.5 }} variant="caption">
                Term.
              </Typography>
              <Stack direction="row" sx={{ mt: '3px' }}>
                {inactiveLocations.length > 0 && controlType !== 'SINGLE' && (
                  <Tooltip
                    enterTouchDelay={10}
                    title="One or more terminals in this Customer Authorization are unable to load"
                  >
                    <Box
                      sx={{
                        backgroundColor: 'error.main',
                        borderRadius: '50%',
                        height: 18,
                        mr: 0.5,
                        position: 'relative',
                        width: 18,
                        zIndex: 0,
                      }}
                    >
                      <ErrorIcon
                        sx={{
                          color: 'error.light',
                          height: 24,
                          left: -3,
                          position: 'absolute',
                          top: -3,
                          width: 24,
                        }}
                      />
                    </Box>
                  </Tooltip>
                )}
                <Typography sx={{ padding: '3px 0', mt: '-3px' }} variant="body2">
                  {terminals.length}
                </Typography>
              </Stack>
            </Stack>
            <Stack
              direction={lgUp ? 'column' : 'row'}
              flex={1}
              sx={{
                alignItems: { xs: 'center', lg: 'start' },
                marginRight: { lg: 1 },
              }}
            >
              <Typography sx={{ mb: { xs: '2px', lg: 0.5 }, mr: 0.5 }} variant="caption">
                Dest.
              </Typography>

              <Typography sx={{ padding: { lg: '3px 0' } }} variant="body2">
                {controls.length}
              </Typography>
            </Stack>
          </Stack>
          <Stack
            direction={lgUp ? 'column' : 'row'}
            flex={1}
            sx={{ alignItems: { xs: 'baseline', lg: 'start' } }}
          >
            <Typography mr={0.5} variant="caption">
              Class
            </Typography>
            <Typography sx={{ padding: { lg: '3px 0' } }} variant="body2">
              {customerClassName ?? ''}
            </Typography>
          </Stack>
        </Box>
        <Box sx={{ alignSelf: 'start', mr: { xs: 1, lg: 2 }, mt: 1 }}>
          <MIconMenu items={getMenuItems()} />
        </Box>
      </CardContent>
    </Card>
  );
};

CustomerAuthCard.propTypes = {
  customerAuthorization: shape({}).isRequired,
  fetchCustomerAuthorization: func.isRequired,
  selectedAuthorizations: arrayOf(shape({})).isRequired,
  setActivationDialogOpen: func.isRequired,
  setConfirmDialogOpen: func.isRequired,
  setConfirmDialogType: func.isRequired,
  setCustomerAuthorizationId: func.isRequired,
  setCustomerAuthorizationToActivate: func.isRequired,
  setCustomerAuthorizationToDeactivate: func.isRequired,
  setIsViewDrawerOpen: func.isRequired,
  setSelectedAuthorizations: func.isRequired,
};

export default CustomerAuthCard;
